import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { LenderApprovalTermsBasis, LenderPerformanceReportInput } from '@trnsact/trnsact-shared-types';
import { useLenderPerformanceApprovalTerms } from '../../../../hooks/useLenderPerformanceApprovalTerms';
import { APPROVAL_TERMS_BASIS_DEFAULT, TERMS_COMPARISON_DEFAULT_SORTING } from '../../../../constants';
import { ConversionTableSorting } from '../../../../types';
import { ApprovalTermsSummaryCards } from './ApprovalTermsSummaryCards';
import { LenderTermsComparisonTable } from './LenderTermsComparisonTable';
import { AverageRateByLenderChart } from './AverageRateByLenderChart';
import { TermDistributionByLenderChart } from './TermDistributionByLenderChart';
import { TotalCostComparisonChart } from './TotalCostComparisonChart';

interface Props {
  input: LenderPerformanceReportInput;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const ApprovalTerms = ({ input, onLoadingChange }: Props) => {
  const [sorting, setSorting] = useState<ConversionTableSorting>(TERMS_COMPARISON_DEFAULT_SORTING);
  const [basis, setBasis] = useState<LenderApprovalTermsBasis>(APPROVAL_TERMS_BASIS_DEFAULT);

  const basisInput = useMemo<LenderPerformanceReportInput>(
    () => ({ ...input, filters: { ...input.filters, approvalTermsBasis: basis } }),
    [input, basis]
  );

  const {
    summary,
    comparison,
    averageRate,
    termDistribution,
    totalCost,
    isSummaryLoading,
    isComparisonLoading,
    isAverageRateLoading,
    isTermDistributionLoading,
    isTotalCostLoading,
  } = useLenderPerformanceApprovalTerms(basisInput, sorting);

  const isLoading =
    isSummaryLoading || isComparisonLoading || isAverageRateLoading || isTermDistributionLoading || isTotalCostLoading;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <Stack gap="1.5rem">
      <ApprovalTermsSummaryCards summary={summary} loading={isSummaryLoading} basis={basis} onBasisChange={setBasis} />

      <Box data-pdf-page-break>
        <LenderTermsComparisonTable
          rows={comparison}
          loading={isComparisonLoading}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <AverageRateByLenderChart rows={averageRate} loading={isAverageRateLoading} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TermDistributionByLenderChart rows={termDistribution} loading={isTermDistributionLoading} />
      </Box>

      <Box data-pdf-page-break data-pdf-no-break>
        <TotalCostComparisonChart rows={totalCost} loading={isTotalCostLoading} />
      </Box>
    </Stack>
  );
};
